/**
 * CityAutocomplete — "the city you live in", with suggestions inside one
 * country. (Citizen first run · D-2)
 *
 * A FREE-TEXT field first and a suggestion list second: what the citizen types
 * is the value, and a suggestion only fills it for them. So a city Google does
 * not list, a search that fails, or an environment without the Places key never
 * blocks anyone — they keep what they typed.
 *
 * The WAI-ARIA 1.2 combobox pattern: the input owns a listbox, arrow keys move
 * `aria-activedescendant`, Enter picks, Escape closes, and the list closes on
 * blur. Suggestions are the server's (`useCitySuggestions`), fetched after the
 * citizen pauses typing.
 */
import { Loader2, MapPin } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { PLACE_QUERY_MIN_LENGTH, useCitySuggestions } from '@tonnie/sdk';
import { cn, Input } from '@tonnie/ui';
import { useEffect, useId, useState, type KeyboardEvent } from 'react';

const DEBOUNCE_MS = 300;

export interface CityAutocompleteProps {
  /** Stable id for the input — the form's `<Label htmlFor>` points at it. */
  id: string;
  value: string;
  onChange: (city: string) => void;
  onBlur?: () => void;
  /** ISO alpha-2. Suggestions are off until a country is chosen. */
  countryCode: string | null | undefined;
  placeholder?: string;
  invalid?: boolean;
  /** `aria-describedby` — the field's error message, when there is one. */
  describedBy?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

export function CityAutocomplete({
  id,
  value,
  onChange,
  onBlur,
  countryCode,
  placeholder,
  invalid,
  describedBy,
  disabled,
  'data-testid': testId,
}: CityAutocompleteProps) {
  const { t } = useTranslation();
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [value]);

  const { data, isFetching, isError } = useCitySuggestions(open ? debounced : '', countryCode);
  const suggestions = data?.suggestions ?? [];
  const searching = open && value.trim().length >= PLACE_QUERY_MIN_LENGTH && isFetching;
  const showList = open && !disabled && value.trim().length >= PLACE_QUERY_MIN_LENGTH;

  useEffect(() => setActive(-1), [debounced, countryCode]);

  const pick = (index: number) => {
    const suggestion = suggestions[index];
    if (!suggestion) return;
    onChange(suggestion.mainText);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!showList || suggestions.length === 0) {
      if (event.key === 'Escape') setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      pick(active);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const optionId = (index: number) => `${listboxId}-option-${index}`;

  return (
    <div className="relative">
      <MapPin
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listboxId}
        aria-activedescendant={showList && active >= 0 ? optionId(active) : undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        autoComplete="address-level2"
        className={cn('h-11 pl-9 pr-9', invalid && 'border-destructive')}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          onBlur?.();
        }}
        onKeyDown={onKeyDown}
      />
      {searching && (
        <Loader2
          className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground motion-reduce:animate-none"
          aria-hidden="true"
        />
      )}
      {showList && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={t('address.citySearch.suggestions')}
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.placeId}
              id={optionId(index)}
              role="option"
              aria-selected={index === active}
              data-testid={testId ? `${testId}-option` : undefined}
              // mousedown, not click: the input's blur closes the list first.
              onMouseDown={(event) => {
                event.preventDefault();
                pick(index);
              }}
              onMouseEnter={() => setActive(index)}
              className={cn(
                'flex cursor-pointer flex-col rounded-sm px-3 py-2 text-sm',
                index === active ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
              )}
            >
              <span className="font-medium">{suggestion.mainText}</span>
              {suggestion.secondaryText && (
                <span className="text-xs text-muted-foreground">{suggestion.secondaryText}</span>
              )}
            </li>
          ))}
          {!searching && suggestions.length === 0 && (
            <li role="presentation" className="px-3 py-2 text-xs text-muted-foreground">
              {isError ? t('address.citySearch.unavailable') : t('address.citySearch.noResults')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
